import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Command } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

const GROUPS = [
  ["customers", "Kunden", (c) => `${c.first_name || ""} ${c.last_name || ""} ${c.company || ""}`.trim() || c.number, (c) => c.number, "/app/kunden"],
  ["sellers", "Verkäufer", (s) => `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.number, (s) => s.number, "/app/verkaeufer"],
  ["products", "Produkte", (p) => p.name, (p) => p.ean || p.brand || "", "/app/produkte"],
  ["articles", "Artikel", (a) => `${a.number} · ${a.name}`, (a) => a.serial || a.imei || "", "/app/bestand"],
  ["purchases", "Ankäufe", (p) => p.number, (p) => p.seller_name || "", "/app/ankauf"],
  ["sales", "Verkäufe", (s) => s.number, (s) => s.customer_name || "", "/app/verkauf"],
  ["invoices", "Rechnungen", (i) => i.number, (i) => i.customer_name || "", "/app/rechnungen"],
];

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(val)}`);
      setResults(data);
    } catch { setResults(null); }
    setLoading(false);
  };

  return (
    <>
      <button
        data-testid="global-search-open"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm text-zinc-500 border border-zinc-200 rounded-lg px-3 py-2 bg-white hover:border-zinc-400 transition-colors w-full sm:w-56 min-h-[44px]"
      >
        <Command className="w-3.5 h-3.5" /> Suchen…
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm font-medium text-zinc-500">Globale Suche</DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-3">
            <input
              autoFocus
              data-testid="global-search-input"
              value={q}
              onChange={(e) => search(e.target.value)}
              placeholder="Kunde, Artikel, IMEI, Rechnungsnummer…"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div className="max-h-96 overflow-y-auto border-t border-zinc-100">
            {loading && <div className="p-4 text-xs text-zinc-400 font-mono">Suche läuft…</div>}
            {results && GROUPS.map(([key, label, titleFn, subFn, href]) =>
              results[key]?.length ? (
                <div key={key} className="py-2">
                  <div className="px-4 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-400">{label}</div>
                  {results[key].map((item) => (
                    <Link key={item.id} to={href} onClick={() => setOpen(false)}
                      data-testid={`search-result-${key}-${item.id}`}
                      className="flex justify-between items-center px-4 py-2 hover:bg-zinc-50 text-sm">
                      <span className="text-zinc-900">{titleFn(item)}</span>
                      <span className="text-xs text-zinc-400 font-mono">{subFn(item)}</span>
                    </Link>
                  ))}
                </div>
              ) : null
            )}
            {results && !loading && GROUPS.every(([k]) => !results[k]?.length) && (
              <div className="p-6 text-sm text-zinc-400 text-center">Keine Treffer gefunden.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
